import { useEffect } from 'react'
import type { GccRenameState } from './useGccRename'
import { GCC_NAME_MAX_LENGTH, validateGccName } from './gccDesignName'

type Props = Pick<GccRenameState, 'value' | 'error' | 'saving' | 'inputRef' | 'setValue' | 'confirm' | 'cancel'>

/** Rename dialog shared by the GCC Dashboard hero ✎ and the GCC Home card ⋮ menu. */
export default function GccRenameModal({ value, error, saving, inputRef, setValue, confirm, cancel }: Props) {
  // Escape closes the dialog even when focus has left the input.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') cancel() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [cancel])

  // Empty / whitespace-only is reported live so a disabled Save is never unexplained.
  const message = error || (value.trim() ? '' : (validateGccName(value) ?? ''))

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={saving ? undefined : cancel}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="gcc-rename-title"
        className="bg-white rounded-xl shadow-xl p-6 w-full max-w-sm mx-4"
        onClick={e => e.stopPropagation()}
      >
        <h2 id="gcc-rename-title" className="text-base font-semibold text-gray-900 mb-4">Rename GCC Design</h2>
        <input
          ref={inputRef}
          type="text"
          value={value}
          maxLength={GCC_NAME_MAX_LENGTH}
          disabled={saving}
          aria-label="Design name"
          aria-invalid={!!message}
          placeholder="Enter design name"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-purple-400 focus:ring-1 focus:ring-purple-300 disabled:opacity-50"
          onChange={e => setValue(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') void confirm() }}
        />
        <div className="flex items-center justify-between mt-1 mb-4">
          {message
            ? <span role="alert" className="text-xs text-red-500">{message}</span>
            : <span />}
          <span className="text-xs text-gray-400">{value.length}/{GCC_NAME_MAX_LENGTH}</span>
        </div>
        <div className="flex gap-2 justify-end">
          <button
            onClick={cancel}
            disabled={saving}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={() => void confirm()}
            disabled={saving || !value.trim()}
            className="px-4 py-2 text-sm font-semibold text-white bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}
